/** Catalog endpoints: books, authors, publishers, genres, reviews. */

import apiBase, { BaseQueryParams, PaginatedResponse } from './api-base';

/**
 * Type definitions for Catalog module
 */
export interface Author {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  nationality?: string;
  biography?: string;
  birth_date?: string;
  death_date?: string;
  book_count?: number;
  created_at?: string;
  updated_at?: string;
}

/** Filters accepted by GET catalog/authors/ on top of search, ordering and paging. */
export interface AuthorQueryParams extends BaseQueryParams {
  has_bio?: boolean;
  has_nationality?: boolean;
  added?: 'this_month';
}

/** GET catalog/authors/summary/: catalogue-wide counts, staff only. */
export interface AuthorSummary {
  total: number;
  with_nationality: number;
  with_bio: number;
  added_this_month: number;
}

export interface Publisher {
  id: number;
  name: string;
  address?: string;
  website?: string;
  email?: string;
  established_year?: number;
  book_count?: number;
  created_at?: string;
  updated_at?: string;
}

/** Filters accepted by GET catalog/publishers/ on top of search, ordering and paging. */
export interface PublisherQueryParams extends BaseQueryParams {
  has_website?: boolean;
  has_email?: boolean;
  added?: 'this_month';
}

/** GET catalog/publishers/summary/: catalogue-wide counts, staff only. */
export interface PublisherSummary {
  total: number;
  with_website: number;
  with_email: number;
  added_this_month: number;
}

export interface Genre {
  id: number;
  name: string;
  description?: string;
  parent_genre?: number | null;
  parent_genre_name?: string | null;
  full_path?: string;
  /** Books tagged with exactly this genre (not its sub-genres). */
  book_count?: number;
  /** Direct sub-genres as "Parent > Child" strings. */
  subgenres?: string[];
  created_at?: string;
  updated_at?: string;
}

/** Filters accepted by GET catalog/genres/ on top of search, ordering and paging. */
export interface GenreQueryParams extends BaseQueryParams {
  has_description?: boolean;
  top_level?: boolean;
  /** A book tagged on the genre itself or on a direct sub-genre. */
  in_use?: boolean;
}

/** GET catalog/genres/summary/: catalogue-wide counts, staff only. */
export interface GenreSummary {
  total: number;
  top_level: number;
  with_description: number;
  in_use: number;
}

export interface DDCClassification {
  id: number;
  number: string;
  name: string;
  level: number;
  parent?: number;
  parent_number?: string;
  children?: DDCClassification[];
}

export interface Book {
  id: number;
  isbn: string;
  title: string;
  subtitle?: string;
  full_title: string;
  authors: Author[];
  publisher_info?: Publisher;
  genres: Genre[];
  ddc_classifications?: DDCClassification[];
  description?: string;
  publication_date?: string;
  language?: string;
  /** Page count (model field ``pages``; the same name on list and detail). */
  pages?: number | null;
  cover_image?: string;
  series?: string;
  series_number?: number;
  edition?: string;
  average_rating?: string;
  total_reviews?: number;
  review_count?: number;
  recent_reviews?: Review[];
  available_copies?: number;
  total_copies?: number;
  borrowed_copies?: number;
  maintenance_copies?: number;
  availability_status?: 'available' | 'limited' | 'unavailable' | string;
  author_names?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Review {
  id: number;
  book: number;
  user: number;
  rating: number;
  review_text: string;
  is_approved: boolean;
  created_at: string;
  updated_at: string;
  reviewer_name?: string;
}

export interface CreateBookData {
  isbn: string;
  title: string;
  subtitle?: string;
  publication_date: string;
  description?: string;
  language: string;
  pages?: number;
  edition?: string;
  series?: string;
  series_number?: number;
  author_ids: number[];
  genre_ids: number[];
  ddc_ids?: number[];
  publisher_id: number;
}

export interface UpdateBookData extends Partial<CreateBookData> {
  id?: never;
  cover_image?: File;
}

export interface CreateAuthorData {
  first_name: string;
  last_name: string;
  biography?: string;
  /** null clears the date on update. */
  birth_date?: string | null;
  death_date?: string | null;
  nationality?: string;
}

export interface CreatePublisherData {
  name: string;
  address?: string;
  website?: string;
  email?: string;
  /** null clears the year on update. */
  established_year?: number | null;
}

export interface CreateGenreData {
  name: string;
  description?: string;
  /** null makes it top level. */
  parent_genre?: number | null;
}

export interface CreateReviewData {
  book: number;
  rating: number;
  review_text: string;
}

export interface BookQueryParams extends BaseQueryParams {
  author_name?: string;
  genre_name?: string;
  publisher_name?: string;
  language?: string;
  min_rating?: number;
  max_rating?: number;
  publication_date__year?: number;
  availability?: 'available' | 'unavailable';
  added?: 'today';
  author?: number;
  genre?: number;
  publisher?: number;
  available_only?: boolean;
  /** A copy's accession number (partial match). */
  accession?: string;
  /** Start of a DDC code: "5" → 500-599, "523" → 523 and 523.x. */
  ddc?: string;
  /** A top-level genre; includes every genre beneath it. */
  category?: number;
  /** Added on or after / on or before this date (YYYY-MM-DD); either end may be open. */
  added_from?: string;
  added_to?: string;
}

/** One record the interpreter picked, with TypeSafe's probability for it. */
export interface InterpretedRecord { id: number; name: string; probability: number }

/** GET catalog/search/interpret/: a free-text Books search read as filters. */
export interface InterpretedSearch {
  query: string;
  filters: {
    accession?: string;
    ddc?: string;
    publisher?: InterpretedRecord;
    category?: InterpretedRecord;
    genre?: InterpretedRecord;
  };
  /** Words left over for title/author search. */
  text: string;
  /** `rules`: exact lookups only; `unavailable`: TypeSafe needed but not configured / failed. */
  source: 'typesafe' | 'rules' | 'unavailable';
}

export interface AdvancedSearchParams {
  query?: string;
  author?: string;
  genre?: string;
  language?: string;
  year_from?: number;
  year_to?: number;
  rating_min?: number;
  rating_max?: number;
}

export interface CatalogOverview {
  counts: {
    books: number;
    authors: number;
    publishers: number;
    genres: number;
    reviews: number;
  };
  recent_books: Book[];
  top_rated_books: Book[];
  popular_genres: Genre[];
}

export interface PopularBooksResponse {
  most_rated: Book[];
  highest_rated: Book[];
  recently_added: Book[];
}

export interface BookStatistics {
  total_books: number;
  total_authors: number;
  total_publishers: number;
  total_genres: number;
  total_reviews: number;
  average_rating: string;
  books_by_language: Record<string, number>;
  books_by_year: Record<string, number>;
}

export interface BookLibrarianStats {
  total_books: number;
  available_count: number;
  unavailable_count: number;
  total_genres: number;
  added_today: number;
}

/** Shelf and reader figures shared by the author and publisher statistics actions. */
export interface CatalogHoldings {
  total_books: number;
  total_copies: number;
  available_copies: number;
  borrowed_copies: number;
  /** 0 when the author's books have no reviews; check total_reviews first. */
  average_rating: number;
  total_reviews: number;
  genres: string[];
  /** Distinct publication years, ascending; null for books without a date. */
  publication_years: (number | null)[];
}

export type AuthorStatistics = CatalogHoldings;

/** GET catalog/publishers/{id}/statistics/. */
export interface PublisherStatistics extends CatalogHoldings {
  total_authors: number;
  /** The authors it publishes most, busiest first (at most 12). */
  authors: { id: number; full_name: string; book_count: number }[];
}

/** GET catalog/genres/{id}/statistics/. Holdings cover the genre and its whole subtree. */
export interface GenreStatistics extends CatalogHoldings {
  /** Books tagged with exactly this genre: the ones that lose it if it is deleted. */
  direct_books: number;
  /** Direct sub-genres; `book_count` covers each one's own subtree. */
  subgenres: { id: number; name: string; book_count: number }[];
}

/**
 * Books API endpoints
 */
/**
 * Catalog Overview API
 */
export const catalogApi = {
  /**
   * Get catalog overview with statistics and highlights
   * @returns Promise with catalog overview data
   */
  getOverview: async (): Promise<CatalogOverview> => {
    const response = await apiBase.get('/catalog/overview/');
    return response.data;
  },

  /**
   * Get catalog statistics
   * @returns Promise with catalog statistics
   */
  getStatistics: async (): Promise<BookStatistics> => {
    const response = await apiBase.get('/catalog/books/statistics/');
    return response.data;
  },
};

/**
 * Books API endpoints
 */
/** GET catalog/books/suggest/: what a search box offers while the reader types. */
export interface BookSuggestion {
  id: number;
  title: string;
  subtitle: string;
  author_names: string[];
  /** Relative media path; build the URL with src/lib/cover-image.ts. */
  cover_image: string | null;
  publication_year: number | null;
  available_copies: number;
}

export interface AuthorSuggestion {
  id: number;
  name: string;
  book_count: number;
}

export interface BookSuggestions {
  query: string;
  books: BookSuggestion[];
  authors: AuthorSuggestion[];
}

export const booksApi = {
  /** Up to six books and three authors for 2+ typed characters, best match first. Public. */
  suggest: async (q: string): Promise<BookSuggestions> => {
    const response = await apiBase.get('/catalog/books/suggest/', { params: { q } });
    return response.data;
  },

  /**
   * Get paginated list of books with filtering
   * @param params - Query parameters
   * @returns Promise with paginated book list
   */
  getBooks: async (params?: BookQueryParams): Promise<PaginatedResponse<Book>> => {
    const response = await apiBase.get('/catalog/books/', { params });
    return response.data;
  },

  /**
   * Read a free-text Books search as filters (exact accession / DDC lookups plus
   * TypeSafe picks for publisher, category and genre). Librarian/admin only.
   */
  interpretSearch: async (q: string): Promise<InterpretedSearch> => {
    const response = await apiBase.get('/catalog/search/interpret/', { params: { q } });
    return response.data;
  },

  /**
   * Get specific book by ID
   * @param id - Book ID
   * @returns Promise with book data
   */
  getBook: async (id: number): Promise<Book> => {
    const response = await apiBase.get(`/catalog/books/${id}/`);
    return response.data;
  },

  /**
   * Create new book
   * @param bookData - Book creation data
   * @returns Promise with created book
   */
  createBook: async (bookData: CreateBookData): Promise<Book> => {
    const response = await apiBase.post('/catalog/books/', bookData);
    return response.data;
  },

  /**
   * Update existing book
   * @param id - Book ID
   * @param bookData - Book update data
   * @returns Promise with updated book
   */
  updateBook: async (id: number, bookData: UpdateBookData): Promise<Book> => {
    if (bookData.cover_image instanceof File) {
      const fd = new FormData();
      (Object.entries(bookData) as [string, unknown][]).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (v instanceof File) { fd.append(k, v); return; }
        if (Array.isArray(v)) { v.forEach((item) => fd.append(k, String(item))); return; }
        fd.append(k, String(v));
      });
      const response = await apiBase.put(`/catalog/books/${id}/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
    const response = await apiBase.put(`/catalog/books/${id}/`, bookData);
    return response.data;
  },

  /**
   * Partially update book
   * @param id - Book ID
   * @param bookData - Partial book data
   * @returns Promise with updated book
   */
  patchBook: async (id: number, bookData: Partial<UpdateBookData>): Promise<Book> => {
    const response = await apiBase.patch(`/catalog/books/${id}/`, bookData);
    return response.data;
  },

  /**
   * Delete book
   * @param id - Book ID
   * @returns Promise with deletion confirmation
   */
  deleteBook: async (id: number): Promise<void> => {
    await apiBase.delete(`/catalog/books/${id}/`);
  },

  /**
   * Get popular books
   * @returns Promise with popular books data
   */
  getPopularBooks: async (): Promise<PopularBooksResponse> => {
    const response = await apiBase.get('/catalog/books/popular/');
    return response.data;
  },

  /**
   * Get featured books
   * @param limit - Number of books to return
   * @returns Promise with featured books
   */
  getFeaturedBooks: async (limit: number = 5): Promise<Book[]> => {
    const response = await apiBase.get('/catalog/books/featured/', { params: { limit } });
    return response.data;
  },

  /**
   * Get recent books
   * @param days - Number of days to look back
   * @returns Promise with recent books
   */
  getRecentBooks: async (days: number = 30): Promise<Book[]> => {
    const response = await apiBase.get('/catalog/books/recent/', { params: { days } });
    return response.data;
  },

  /**
   * Get book recommendations for the current user
   * @param limit - Number of recommendations to return
   * @returns Promise with recommended books
   */
  getRecommendations: async (limit: number = 5): Promise<Book[]> => {
    const response = await apiBase.get('/catalog/books/recommendations/', { params: { limit } });
    return response.data;
  },

  /**
   * Advanced book search
   * @param searchParams - Advanced search parameters
   * @returns Promise with search results
   */
  advancedSearch: async (searchParams: AdvancedSearchParams): Promise<PaginatedResponse<Book>> => {
    // A read: GET with query parameters (cacheable, bookmarkable, browser-retried).
    const response = await apiBase.get('/catalog/books/advanced-search/', { params: searchParams });
    return response.data;
  },

  /**
   * Get book reviews
   * @param id - Book ID
   * @param params - Query parameters
   * @returns Promise with book reviews
   */
  getBookReviews: async (id: number, params?: BaseQueryParams): Promise<PaginatedResponse<Review>> => {
  // Backend book-level reviews endpoint removed; use global reviews endpoint filtered by book id
  const response = await apiBase.get('/catalog/reviews/', { params: { ...params, book: id } });
  return response.data;
  },

  /**
   * Add a review for a book
   * @param id - Book ID
   * @param reviewData - Review data
   * @returns Promise with created review
   */
  addBookReview: async (id: number, reviewData: CreateReviewData): Promise<Review> => {
    const response = await apiBase.post(`/catalog/books/${id}/add_review/`, reviewData);
    return response.data;
  },
  /**
   * Get book statistics (counts, availability, recent additions)
   * @returns Promise with book statistics
   */
  getStatistics: async (): Promise<BookLibrarianStats> => {
    const response = await apiBase.get('/catalog/books/statistics/');
    return response.data;
  },
};
/** `{id, name}` rows from the `options/` projection (pickers and typeaheads). */
export interface OptionRow {
  id: number;
  name: string;
}

export const referenceOptionsApi = {
  authors: async (params?: { search?: string; page_size?: number }): Promise<PaginatedResponse<OptionRow>> => {
    const response = await apiBase.get('/catalog/authors/options/', { params });
    return response.data;
  },
  publishers: async (params?: { search?: string; page_size?: number }): Promise<PaginatedResponse<OptionRow>> => {
    const response = await apiBase.get('/catalog/publishers/options/', { params });
    return response.data;
  },
  genres: async (params?: { search?: string; page_size?: number }): Promise<PaginatedResponse<OptionRow>> => {
    const response = await apiBase.get('/catalog/genres/options/', { params });
    return response.data;
  },
};

export const authorsApi = {
  /**
   * Get paginated list of authors
   * @param params - Query parameters
   * @returns Promise with paginated author list
   */
  getAuthors: async (params?: AuthorQueryParams): Promise<PaginatedResponse<Author>> => {
    const response = await apiBase.get('/catalog/authors/', { params });
    return response.data;
  },

  /**
   * Get specific author by ID
   * @param id - Author ID
   * @returns Promise with author data
   */
  getAuthor: async (id: number): Promise<Author> => {
    const response = await apiBase.get(`/catalog/authors/${id}/`);
    return response.data;
  },

  /**
   * Create new author with authentication token
   * @param authorData - Author creation data
   * @returns Promise with created author
   * @note Authentication token is automatically included by apiBase interceptor
   */
  createAuthor: async (authorData: CreateAuthorData): Promise<Author> => {
    const response = await apiBase.post('/catalog/authors/', authorData);
    console.debug('Author created successfully:', response.data.id);
    return response.data;
  },

  /**
   * Update existing author
   * @param id - Author ID
   * @param authorData - Author update data
   * @returns Promise with updated author
   */
  updateAuthor: async (id: number, authorData: Partial<CreateAuthorData>): Promise<Author> => {
    const response = await apiBase.put(`/catalog/authors/${id}/`, authorData);
    return response.data;
  },

  /**
   * Delete author
   * @param id - Author ID
   */
  deleteAuthor: async (id: number): Promise<void> => {
    await apiBase.delete(`/catalog/authors/${id}/`);
  },

  /**
   * Get author's books
   * @param id - Author ID
   * @param params - Query parameters
   * @returns Promise with author's books
   */
  getAuthorBooks: async (id: number, params?: BaseQueryParams): Promise<PaginatedResponse<Book>> => {
    const response = await apiBase.get(`/catalog/authors/${id}/books/`, { params });
    return response.data;
  },

  /**
   * Get detailed statistics for an author
   * @param id - Author ID
   * @returns Promise with author statistics
   */
  getAuthorStatistics: async (id: number): Promise<AuthorStatistics> => {
    const response = await apiBase.get(`/catalog/authors/${id}/statistics/`);
    return response.data;
  },

  /**
   * Catalogue-wide author counts for the staff Authors page
   * @returns Promise with total / with_nationality / with_bio / added_this_month
   */
  getAuthorSummary: async (): Promise<AuthorSummary> => {
    const response = await apiBase.get('/catalog/authors/summary/');
    return response.data;
  },
};

/**
 * Publishers API endpoints
 */
export const publishersApi = {
  /**
   * Get paginated list of publishers
   * @param params - Query parameters
   * @returns Promise with paginated publisher list
   */
  getPublishers: async (params?: PublisherQueryParams): Promise<PaginatedResponse<Publisher>> => {
    const response = await apiBase.get('/catalog/publishers/', { params });
    return response.data;
  },

  /**
   * Get specific publisher by ID
   * @param id - Publisher ID
   * @returns Promise with publisher data
   */
  getPublisher: async (id: number): Promise<Publisher> => {
    const response = await apiBase.get(`/catalog/publishers/${id}/`);
    return response.data;
  },

  /**
   * Create new publisher with authentication token
   * @param publisherData - Publisher creation data
   * @returns Promise with created publisher
   * @note Authentication token is automatically included by apiBase interceptor
   */
  createPublisher: async (publisherData: CreatePublisherData): Promise<Publisher> => {
    const response = await apiBase.post('/catalog/publishers/', publisherData);
    console.debug('Publisher created successfully:', response.data.id);
    return response.data;
  },

  /**
   * Update existing publisher
   * @param id - Publisher ID
   * @param publisherData - Publisher update data
   * @returns Promise with updated publisher
   */
  updatePublisher: async (id: number, publisherData: Partial<CreatePublisherData>): Promise<Publisher> => {
    const response = await apiBase.put(`/catalog/publishers/${id}/`, publisherData);
    return response.data;
  },

  /**
   * Delete publisher
   * @param id - Publisher ID
   */
  deletePublisher: async (id: number): Promise<void> => {
    await apiBase.delete(`/catalog/publishers/${id}/`);
  },

  /**
   * Get publisher's books
   * @param id - Publisher ID
   * @param params - Query parameters
   * @returns Promise with publisher's books
   */
  getPublisherBooks: async (id: number, params?: BaseQueryParams): Promise<PaginatedResponse<Book>> => {
    const response = await apiBase.get(`/catalog/publishers/${id}/books/`, { params });
    return response.data;
  },

  /**
   * Holdings for a publisher and the authors it publishes most
   * @param id - Publisher ID
   */
  getPublisherStatistics: async (id: number): Promise<PublisherStatistics> => {
    const response = await apiBase.get(`/catalog/publishers/${id}/statistics/`);
    return response.data;
  },

  /**
   * Catalogue-wide publisher counts for the staff Publishers page
   */
  getPublisherSummary: async (): Promise<PublisherSummary> => {
    const response = await apiBase.get('/catalog/publishers/summary/');
    return response.data;
  },
};

/**
 * Genres API endpoints
 */
export const genresApi = {
  /**
   * Get paginated list of genres
   * @param params - Query parameters
   * @returns Promise with paginated genre list
   */
  getGenres: async (params?: GenreQueryParams): Promise<PaginatedResponse<Genre>> => {
    const response = await apiBase.get('/catalog/genres/', { params });
    return response.data;
  },

  /**
   * Get specific genre by ID
   * @param id - Genre ID
   * @returns Promise with genre data
   */
  getGenre: async (id: number): Promise<Genre> => {
    const response = await apiBase.get(`/catalog/genres/${id}/`);
    return response.data;
  },

  /**
   * Create new genre
   * @param genreData - Genre creation data
   * @returns Promise with created genre
   */
  createGenre: async (genreData: CreateGenreData): Promise<Genre> => {
    const response = await apiBase.post('/catalog/genres/', genreData);
    return response.data;
  },

  /**
   * Update existing genre
   * @param id - Genre ID
   * @param genreData - Genre update data
   * @returns Promise with updated genre
   */
  updateGenre: async (id: number, genreData: Partial<CreateGenreData>): Promise<Genre> => {
    const response = await apiBase.put(`/catalog/genres/${id}/`, genreData);
    return response.data;
  },

  /**
   * Get genre hierarchy
   * @returns Promise with genre hierarchy
   */
  getGenreHierarchy: async (): Promise<Genre[]> => {
    const response = await apiBase.get('/catalog/genres/hierarchy/');
    return response.data;
  },

  /**
   * Get genre's books
   * @param id - Genre ID
   * @param params - Query parameters
   * @returns Promise with genre's books
   */
  getGenreBooks: async (id: number, params?: BaseQueryParams): Promise<PaginatedResponse<Book>> => {
    const response = await apiBase.get(`/catalog/genres/${id}/books/`, { params });
    return response.data;
  },

  /**
   * Get detailed statistics for a genre
   * @param id - Genre ID
   * @returns Promise with genre statistics
   */
  getGenreStatistics: async (id: number): Promise<GenreStatistics> => {
    const response = await apiBase.get(`/catalog/genres/${id}/statistics/`);
    return response.data;
  },

  /**
   * Delete a genre. Its sub-genres move up to its parent; its books lose it.
   */
  deleteGenre: async (id: number): Promise<void> => {
    await apiBase.delete(`/catalog/genres/${id}/`);
  },

  /**
   * Catalogue-wide genre counts for the staff Genres page
   */
  getGenreSummary: async (): Promise<GenreSummary> => {
    const response = await apiBase.get('/catalog/genres/summary/');
    return response.data;
  },
};

/**
 * Reviews API endpoints
 */
export const reviewsApi = {
  /**
   * Get paginated list of reviews
   * @param params - Query parameters
   * @returns Promise with paginated review list
   */
  getReviews: async (params?: BaseQueryParams & { book?: number; rating?: number; is_approved?: boolean }): Promise<PaginatedResponse<Review>> => {
    const response = await apiBase.get('/catalog/reviews/', { params });
    return response.data;
  },

  /**
   * Get specific review by ID
   * @param id - Review ID
   * @returns Promise with review data
   */
  getReview: async (id: number): Promise<Review> => {
    const response = await apiBase.get(`/catalog/reviews/${id}/`);
    return response.data;
  },

  /**
   * Create new review
   * @param reviewData - Review creation data
   * @returns Promise with created review
   */
  createReview: async (reviewData: CreateReviewData): Promise<Review> => {
    const response = await apiBase.post('/catalog/reviews/', reviewData);
    return response.data;
  },

  /**
   * Update existing review
   * @param id - Review ID
   * @param reviewData - Review update data
   * @returns Promise with updated review
   */
  updateReview: async (id: number, reviewData: Partial<CreateReviewData>): Promise<Review> => {
    const response = await apiBase.put(`/catalog/reviews/${id}/`, reviewData);
    return response.data;
  },

  /**
   * Partially update review
   * @param id - Review ID
   * @param reviewData - Partial review data
   * @returns Promise with updated review
   */
  patchReview: async (id: number, reviewData: Partial<CreateReviewData>): Promise<Review> => {
    const response = await apiBase.patch(`/catalog/reviews/${id}/`, reviewData);
    return response.data;
  },

  /**
   * Delete review
   * @param id - Review ID
   * @returns Promise with deletion confirmation
   */
  deleteReview: async (id: number): Promise<void> => {
    await apiBase.delete(`/catalog/reviews/${id}/`);
  },

  /**
   * Approve review (Staff only)
   * @param id - Review ID
   * @returns Promise with approved review
   */
  approveReview: async (id: number): Promise<Review> => {
    const response = await apiBase.post(`/catalog/reviews/${id}/approve/`);
    return response.data;
  },

  /**
   * Unapprove review (Staff only)
   * @param id - Review ID
   * @returns Promise with unapproved review
   */
  unapproveReview: async (id: number): Promise<Review> => {
    const response = await apiBase.post(`/catalog/reviews/${id}/unapprove/`);
    return response.data;
  },

  /**
   * Get current user's reviews
   * @param params - Query parameters
   * @returns Promise with user's reviews
   */
  getMyReviews: async (params?: BaseQueryParams): Promise<PaginatedResponse<Review>> => {
    const response = await apiBase.get('/catalog/reviews/my_reviews/', { params });
    return response.data;
  },
};

export const ddcApi = {
  /** Flat paginated list: supports ?search= and ?page_size= */
  list: async (params?: { search?: string; page_size?: number; parent?: number | null }): Promise<PaginatedResponse<DDCClassification>> => {
    const response = await apiBase.get('/catalog/ddc/', { params });
    return response.data;
  },

  /** Ten main classes, each with their direct children */
  mainClasses: async (): Promise<DDCClassification[]> => {
    const response = await apiBase.get('/catalog/ddc/main-classes/');
    return response.data;
  },

  /** Direct children of a given parent (pass null for root main classes) */
  byParent: async (parentId: number | null): Promise<DDCClassification[]> => {
    const params = parentId === null ? { parent: 'null' } : { parent: parentId };
    const response = await apiBase.get('/catalog/ddc/by-parent/', { params });
    return response.data;
  },

  /** Fetch a single DDC entry by ID */
  getById: async (id: number): Promise<DDCClassification> => {
    const response = await apiBase.get(`/catalog/ddc/${id}/`);
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// External-record resolution (TypeSafe-backed, librarian/admin only)
// Backend: catalog/resolvers.py via POST /catalog/external/resolve/ and /rerank/
// ---------------------------------------------------------------------------

export interface ExternalRecordResolveRequest {
  title: string;
  subtitle?: string | null;
  authors?: string[];
  publisher?: string | null;
  description?: string | null;
  subjects?: string[];
  isbn?: string;
}

/** Same-entity band from catalog.resolvers.band_for: link >= 1.75, confirm >= 0.5, else create. */
export type ResolutionBand = 'link' | 'confirm' | 'create';

export interface ResolvedGenre {
  genre_id: number | null;
  genre_name: string | null;
  ddc_id: number | null;
  ddc_number: string | null;
  /** Choice confidence 0-1: how concentrated the probability mass is on genre_id. */
  confidence: number;
  is_fiction: number | null;
  suggestions: Array<{ genre_id: number; genre_name: string; ddc_number: string | null; probability: number }>;
  source: 'typesafe' | 'unavailable' | 'no_genres';
}

export interface ResolvedAuthorCandidate {
  author_id: number;
  name: string;
  score: number;
  band: ResolutionBand;
}

export interface ResolvedAuthor {
  name: string;
  first_name: string;
  last_name: string;
  strategy: 'last_1' | 'last_2' | 'inverted' | 'suffix' | 'whole';
  strategy_confidence: number;
  source: 'typesafe' | 'rule' | 'unavailable';
  /** Set only when an existing row scored in the link band. */
  match: ResolvedAuthorCandidate | null;
  /** Link + confirm band candidates, best first. */
  candidates: ResolvedAuthorCandidate[];
}

export interface ResolvedPublisherCandidate {
  publisher_id: number;
  name: string;
  score: number;
  band: ResolutionBand;
}

export interface ResolvedPublisher {
  name: string;
  match: ResolvedPublisherCandidate | null;
  candidates: ResolvedPublisherCandidate[];
}

export interface ExternalRecordResolveResponse {
  available: boolean;
  genre: ResolvedGenre;
  authors: ResolvedAuthor[];
  publisher: ResolvedPublisher | null;
}

export interface ExternalSearchRerankResponse {
  available: boolean;
  /** score is 0-3 (catalog.resolvers.RERANK_LEVELS); null when TypeSafe was unavailable. */
  results: Array<{ index: number; score: number | null; confidence: number | null }>;
}

export const externalResolutionApi = {
  resolve: async (payload: ExternalRecordResolveRequest): Promise<ExternalRecordResolveResponse> => {
    const response = await apiBase.post('/catalog/external/resolve/', payload);
    return response.data;
  },

  rerank: async (query: string, results: Array<Record<string, unknown>>): Promise<ExternalSearchRerankResponse> => {
    const response = await apiBase.post('/catalog/external/rerank/', { query, results });
    return response.data;
  },
};
